import { useCallback, useRef, useState } from "react";
import { Popover } from "react-aria-components";

import { Bulb, Gear, Lock, Magnify } from "components/icons/generated";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DropdownSectionItem from "ds/components/Dropdown/SectionItem";
import { IconComponent } from "types/Icon";

import useAssistantPanelOverlayDismiss from "../../hooks/useOverlayDismiss";
import { ChatMode } from "../types";
import styles from "./styles.module.css";

type ChatExamplesCategoryKey = "troubleshoot" | "understand" | "find" | "access";

type ChatExamplesCategory = {
  key: ChatExamplesCategoryKey;
  label: string;
  icon: IconComponent;
  examples: { prompt: string; mode: ChatMode }[];
};

const CATEGORIES: ChatExamplesCategory[] = [
  {
    key: "troubleshoot",
    label: "Troubleshoot",
    icon: Gear,
    examples: [
      { prompt: "Why did my run fail with this error?", mode: ChatMode.ASK },
      {
        prompt: "How do I fix this Terraform state error - lock, invalid resource, or import?",
        mode: ChatMode.ASK,
      },
      {
        prompt: "Why did my run execute against an old commit instead of HEAD?",
        mode: ChatMode.ASK,
      },
      {
        prompt: 'How do I fix an "unauthorized - could not create stack" error?',
        mode: ChatMode.ASK,
      },
      { prompt: "Why is my plan showing unexpected resource changes?", mode: ChatMode.ASK },
    ],
  },
  {
    key: "understand",
    label: "Understand",
    icon: Bulb,
    examples: [
      { prompt: "How do Spacelift Spaces and their hierarchy work?", mode: ChatMode.ASK },
      {
        prompt: "What's the difference between private and public worker pools?",
        mode: ChatMode.ASK,
      },
      { prompt: "What does this Rego / login policy actually do?", mode: ChatMode.ASK },
      { prompt: "How does drift detection work, and when does it run?", mode: ChatMode.ASK },
      { prompt: "How do Terraform and OpenTofu differ in Spacelift?", mode: ChatMode.ASK },
    ],
  },
  {
    key: "find",
    label: "Find",
    icon: Magnify,
    examples: [
      { prompt: "List all my stacks, filtered by name or label", mode: ChatMode.ASK },
      { prompt: "Which stacks are least active, by last apply date?", mode: ChatMode.ASK },
      { prompt: "Show me every run currently in progress", mode: ChatMode.ASK },
      { prompt: "How many stacks consume this module?", mode: ChatMode.ASK },
      { prompt: "How many modules are in my registry, and what versions?", mode: ChatMode.ASK },
    ],
  },
  {
    key: "access",
    label: "Access & Policy",
    icon: Lock,
    examples: [
      { prompt: "Set up RBAC space restrictions and per-space access levels", mode: ChatMode.ASK },
      { prompt: "How do I create an API key or read-only access token?", mode: ChatMode.ASK },
      { prompt: "Whose approval is required for this run under my policy?", mode: ChatMode.ASK },
      {
        prompt: "Write a notification policy that alerts Slack when a run fails",
        mode: ChatMode.ASK,
      },
      { prompt: "How do I set up the GitHub App VCS integration?", mode: ChatMode.ASK },
    ],
  },
];

type ChatExamplesProps = {
  onPromptSelect: (prompt: string, mode: ChatMode) => void;
};

const ChatExamples = ({ onPromptSelect }: ChatExamplesProps) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const [openKey, setOpenKey] = useState<ChatExamplesCategoryKey | undefined>();

  const openCategory = CATEGORIES.find((category) => category.key === openKey);

  const closePopover = useCallback(() => setOpenKey(undefined), []);

  const { wrapperRef, overlayProps } = useAssistantPanelOverlayDismiss({
    isOpen: openKey !== undefined,
    onClose: closePopover,
    swallowTriggerPress: false,
  });

  const handlePromptSelect = (prompt: string, mode: ChatMode) => {
    onPromptSelect(prompt, mode);
    setOpenKey(undefined);
  };

  return (
    <Box ref={wrapperRef} direction="column" align="center" padding="medium x-large 0">
      <Box ref={rowRef} className={styles.row} gap="medium" wrap justify="center">
        {CATEGORIES.map(({ key, label, icon }) => (
          <Button
            key={key}
            variant="secondary"
            size="small"
            startIcon={icon}
            active={openKey === key}
            onPress={() => setOpenKey((current) => (current === key ? undefined : key))}
          >
            {label}
          </Button>
        ))}
      </Box>

      <Popover
        isNonModal
        triggerRef={rowRef}
        isOpen={openKey !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            closePopover();
          }
        }}
        placement="bottom left"
        className={styles.popover}
        {...overlayProps}
      >
        <Box role="menu" aria-label={openCategory?.label} direction="column" padding="small">
          {openCategory?.examples.map(({ prompt, mode }) => (
            <DropdownSectionItem
              key={prompt}
              className={styles.prompt}
              onClick={() => handlePromptSelect(prompt, mode)}
            >
              {prompt}
            </DropdownSectionItem>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default ChatExamples;
